import React, { useState, useEffect, useRef } from "react";
import Sidebar from "../../components/Sidebar";
import "./upload_data_company.css";
import {
  FaCloudUploadAlt,
  FaSpinner,
  FaTimes,
  FaCheckCircle,
  FaSave,
  FaFileAlt,
  FaImage,
  FaFilePdf,
  FaReceipt,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  takeImageUploadExpenseApi,
  saveExpenseApi,
  createNewInvoiceApi,
  recordMileageApi,
  createBankStatementApi,
  categoryApi,
} from "../../services/apiCall";

export default function UploadDataCompanyPage() {
  const businessId = useSelector((state) => state?.user?.businessId);
  const userId = useSelector((state) => state?.user?.userId);
  const businessName =
    useSelector((state) => state?.user?.businessName) || "Your Business";

  const [categories, setCategories] = useState([]);
  const [uploadType, setUploadType] = useState("expense");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadDone, setUploadDone] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isSavingUpload, setIsSavingUpload] = useState(false);
  const fileInputRef = useRef(null);
  const [extracted, setExtracted] = useState(null);

  const uploadTypeOptions = [
    { value: "expense", label: "Expense", icon: "💸" },
    { value: "sale", label: "Sales Invoice", icon: "📄" },
    { value: "mileage", label: "Mileage", icon: "🚗" },
    { value: "bank", label: "Bank Statement", icon: "🏦" },
  ];

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await categoryApi();
        setCategories(res?.data?.expenseCategories ?? []);
      } catch (err) {}
    };
    loadCategories();
  }, []);

  const processFile = async (file) => {
    if (!file) return;
    setUploadFile(file);
    setUploadDone(false);
    setExtracted(null);

    // Preview
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => setUploadPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setUploadPreview(null);
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("userId", userId);
    formData.append("businessId", businessId);
    formData.append("type", uploadType);

    try {
      const response = await takeImageUploadExpenseApi(formData);
      const data = response?.data?.data;
      if (data) {
        setExtracted(data);
        setUploadDone(true);
        toast.success("File processed — data extracted!");
      } else {
        toast.info("File uploaded, please fill in the details below.");
        setExtracted({});
        setUploadDone(true);
      }
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Failed to extract data from file.");
      setExtracted({});
      setUploadDone(true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };
  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);

  const handleFieldChange = (key, value) => {
    setExtracted((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveExtracted = async () => {
    if (!extracted) {
      toast.error("No data to save.");
      return;
    }

    setIsSavingUpload(true);
    try {
      let response;

      if (uploadType === "expense") {
        const payload = {
          businessId,
          createdBy: userId,
          category: extracted.category || "",
          description: extracted.description || extracted.notes || "",
          gross: parseFloat(extracted.gross) || 0,
          vat: parseFloat(extracted.vat) || 0,
          net: parseFloat(extracted.net) || 0,
          transactionDate:
            extracted.createdAt
              ? new Date(extracted.createdAt).toISOString().split("T")[0]
              : new Date().toISOString().split("T")[0],
        };
        response = await saveExpenseApi(payload);
        if (response?.data?.expense) toast.success("Expense saved!");
        else toast.error("Failed to save expense.");
      } else if (uploadType === "sale") {
        const payload = {
          businessId,
          createdBy: userId,
          invoiceNumber: extracted.invoiceNumber || `INV-${Date.now().toString().slice(-6)}`,
          gross: parseFloat(extracted.gross) || 0,
          vat: parseFloat(extracted.vat) || 0,
          net: parseFloat(extracted.net) || 0,
        };
        response = await createNewInvoiceApi(payload);
        if (response?.data) toast.success("Sales invoice saved!");
        else toast.error("Failed to save invoice.");
      } else if (uploadType === "mileage") {
        const payload = {
          businessId,
          createdBy: userId,
          reason: extracted.reason || "",
          description: extracted.description || "",
          from: extracted.from || "",
          to: extracted.to || "",
          miles: parseFloat(extracted.miles) || 0,
        };
        response = await recordMileageApi(payload);
        if (response?.data?.mileage) toast.success("Mileage record saved!");
        else toast.error("Failed to save mileage.");
      } else if (uploadType === "bank") {
        const payload = {
          businessId,
          createdBy: userId,
          transactionDate:
            extracted.createdAt
              ? new Date(extracted.createdAt).toISOString().split("T")[0]
              : new Date().toISOString().split("T")[0],
          referenceNumber: extracted.referenceNumber || `REF-${Date.now().toString().slice(-6)}`,
          transactionType: extracted.transactionType || "CREDIT",
          amount: parseFloat(extracted.amount) || 0,
          balance: parseFloat(extracted.balance) || 0,
          category: extracted.category || "",
          description: extracted.description || "",
          accountId: "121212",
        };
        response = await createBankStatementApi(payload);
        if (response?.data?.statement) toast.success("Bank statement saved!");
        else toast.error("Failed to save bank statement.");
      }

      setUploadFile(null);
      setUploadPreview(null);
      setExtracted(null);
      setUploadDone(false);
    } catch (err) {
      toast.error(err?.message || "Failed to save. Please try again.");
    } finally {
      setIsSavingUpload(false);
    }
  };

  const getFileIcon = (file) => {
    if (!file) return <FaFileAlt />;
    if (file.type.startsWith("image/")) return <FaImage className="file-type-icon img" />;
    if (file.type === "application/pdf") return <FaFilePdf className="file-type-icon pdf" />;
    return <FaFileAlt className="file-type-icon doc" />;
  };

  const renderExtractedFields = () => {
    if (!extracted) return null;

    if (uploadType === "expense") {
      return (
        <div className="extracted-grid-ud">
          <div className="ext-field-ud">
            <label>Category</label>
            <select value={extracted.category || ""} onChange={(e) => handleFieldChange("category", e.target.value)}>
              <option value="">Select category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="ext-field-ud">
            <label>Gross Amount (£)</label>
            <input type="number" placeholder="0.00" value={extracted.gross || ""} onChange={(e) => handleFieldChange("gross", e.target.value)} />
          </div>
          <div className="ext-field-ud">
            <label>VAT (£)</label>
            <input type="number" placeholder="0.00" value={extracted.vat || ""} onChange={(e) => handleFieldChange("vat", e.target.value)} />
          </div>
          <div className="ext-field-ud">
            <label>Net Amount (£)</label>
            <input type="number" placeholder="0.00" value={extracted.net || ""} onChange={(e) => handleFieldChange("net", e.target.value)} />
          </div>
          <div className="ext-field-ud full-ud">
            <label>Description / Notes</label>
            <textarea rows={2} placeholder="Description…" value={extracted.description || ""} onChange={(e) => handleFieldChange("description", e.target.value)} />
          </div>
        </div>
      );
    }
    if (uploadType === "sale") {
      return (
        <div className="extracted-grid-ud">
          <div className="ext-field-ud">
            <label>Invoice Number</label>
            <input type="text" placeholder="INV-001" value={extracted.invoiceNumber || ""} onChange={(e) => handleFieldChange("invoiceNumber", e.target.value)} />
          </div>
          <div className="ext-field-ud">
            <label>Gross Amount (£)</label>
            <input type="number" placeholder="0.00" value={extracted.gross || ""} onChange={(e) => handleFieldChange("gross", e.target.value)} />
          </div>
          <div className="ext-field-ud">
            <label>VAT (£)</label>
            <input type="number" placeholder="0.00" value={extracted.vat || ""} onChange={(e) => handleFieldChange("vat", e.target.value)} />
          </div>
          <div className="ext-field-ud">
            <label>Net Amount (£)</label>
            <input type="number" placeholder="0.00" value={extracted.net || ""} onChange={(e) => handleFieldChange("net", e.target.value)} />
          </div>
        </div>
      );
    }
    if (uploadType === "mileage") {
      return (
        <div className="extracted-grid-ud">
          <div className="ext-field-ud">
            <label>Reason for Travel</label>
            <input type="text" placeholder="e.g. Client Meeting" value={extracted.reason || ""} onChange={(e) => handleFieldChange("reason", e.target.value)} />
          </div>
          <div className="ext-field-ud">
            <label>Miles</label>
            <input type="number" placeholder="0" value={extracted.miles || ""} onChange={(e) => handleFieldChange("miles", e.target.value)} />
          </div>
          <div className="ext-field-ud">
            <label>From</label>
            <input type="text" placeholder="Start location" value={extracted.from || ""} onChange={(e) => handleFieldChange("from", e.target.value)} />
          </div>
          <div className="ext-field-ud">
            <label>To</label>
            <input type="text" placeholder="Destination" value={extracted.to || ""} onChange={(e) => handleFieldChange("to", e.target.value)} />
          </div>
          <div className="ext-field-ud full-ud">
            <label>Description</label>
            <textarea rows={2} placeholder="Details…" value={extracted.description || ""} onChange={(e) => handleFieldChange("description", e.target.value)} />
          </div>
        </div>
      );
    }
    if (uploadType === "bank") {
      return (
        <div className="extracted-grid-ud">
          <div className="ext-field-ud">
            <label>Reference Number</label>
            <input type="text" placeholder="REF12345" value={extracted.referenceNumber || ""} onChange={(e) => handleFieldChange("referenceNumber", e.target.value)} />
          </div>
          <div className="ext-field-ud">
            <label>Transaction Type</label>
            <select value={extracted.transactionType || ""} onChange={(e) => handleFieldChange("transactionType", e.target.value)}>
              <option value="">Select type</option>
              <option value="CREDIT">CREDIT</option>
              <option value="DEBIT">DEBIT</option>
            </select>
          </div>
          <div className="ext-field-ud">
            <label>Amount (£)</label>
            <input type="number" placeholder="0.00" value={extracted.amount || ""} onChange={(e) => handleFieldChange("amount", e.target.value)} />
          </div>
          <div className="ext-field-ud">
            <label>Balance (£)</label>
            <input type="number" placeholder="0.00" value={extracted.balance || ""} onChange={(e) => handleFieldChange("balance", e.target.value)} />
          </div>
          <div className="ext-field-ud">
            <label>Category</label>
            <input type="text" placeholder="e.g. Office Supplies" value={extracted.category || ""} onChange={(e) => handleFieldChange("category", e.target.value)} />
          </div>
          <div className="ext-field-ud full-ud">
            <label>Description</label>
            <textarea rows={2} placeholder="Description…" value={extracted.description || ""} onChange={(e) => handleFieldChange("description", e.target.value)} />
          </div>
        </div>
      );
    }
    return null;
  };

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  return (
    <div className="app">
      <Sidebar />
      <main className="main">
        <header className="dashboard-header">
          <div>
            <h2 className="dashboard-title">Upload Data</h2>
            <p className="dashboard-sub">Upload documents or images — we'll extract the data</p>
          </div>
          <div className="dashboard-info">
            <h3 className="dashboard-company">{businessName}</h3>
            <p className="dashboard-date">{today}</p>
          </div>
        </header>

        <section className="upload-data-container-standalone">
          <div className="upload-data-header-ud">
            <h3>Select Data Type</h3>
            <p>Choose what type of record you are uploading before attaching the file.</p>
          </div>

          <div className="upload-type-selector-ud">
            {uploadTypeOptions.map((opt) => (
              <button
                key={opt.value}
                className={`type-option-ud ${uploadType === opt.value ? "active" : ""}`}
                onClick={() => {
                  setUploadType(opt.value);
                  setUploadFile(null);
                  setUploadPreview(null);
                  setExtracted(null);
                  setUploadDone(false);
                }}
              >
                <span className="type-icon-ud">{opt.icon}</span>
                <span>{opt.label}</span>
              </button>
            ))}
          </div>

          <div
            className={`upload-drop-zone-ud ${isDragging ? "dragging" : ""} ${uploadDone ? "done" : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !uploadFile && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf,.csv,.xlsx,.xls,.doc,.docx"
              style={{ display: "none" }}
              onChange={handleFileInputChange}
            />

            {!uploadFile ? (
              <>
                <div className="drop-icon-wrap-ud">
                  <FaCloudUploadAlt />
                </div>
                <h3>Drop your file here</h3>
                <p>or <span className="browse-link-ud">browse to upload</span></p>
                <span className="drop-hint-ud">Accepts: Images, PDF, CSV, XLSX, DOC</span>
              </>
            ) : (
              <div className="upload-file-status-ud">
                <div className="file-info-row-ud">
                  {getFileIcon(uploadFile)}
                  <div className="file-meta-ud">
                    <span className="file-name-ud">{uploadFile.name}</span>
                    <span className="file-size-ud">{(uploadFile.size / 1024).toFixed(1)} KB</span>
                  </div>
                  <button
                    className="file-remove-btn-ud"
                    onClick={(e) => {
                      e.stopPropagation();
                      setUploadFile(null);
                      setUploadPreview(null);
                      setExtracted(null);
                      setUploadDone(false);
                    }}
                  >
                    <FaTimes />
                  </button>
                </div>

                {isUploading && (
                  <div className="processing-indicator-ud">
                    <FaSpinner className="spin" />
                    <span>Extracting data from your file…</span>
                  </div>
                )}

                {uploadDone && !isUploading && (
                  <div className="processing-done-ud">
                    <FaCheckCircle />
                    <span>Data extracted successfully</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {uploadPreview && (
            <div className="upload-img-preview-ud">
              <img src={uploadPreview} alt="Document preview" />
            </div>
          )}

          {uploadDone && extracted !== null && (
            <div className="extracted-data-card-ud">
              <div className="extracted-data-header-ud">
                <FaReceipt />
                <h4>Extracted Data — Review & Edit</h4>
              </div>

              {renderExtractedFields()}

              <div className="extracted-save-row-ud">
                <button
                  className="btn-save-extracted-ud"
                  onClick={handleSaveExtracted}
                  disabled={isSavingUpload}
                >
                  {isSavingUpload ? (
                    <><FaSpinner className="spin" /> Saving…</>
                  ) : (
                    <><FaSave /> Save {uploadTypeOptions.find((o) => o.value === uploadType)?.label}</>
                  )}
                </button>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
